import { ChangeEvent, ReactNode, useCallback, useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { Timeframe, TimeframeToHours, TimeframeUnit } from "@/Common/Models/Timeframe";

import "./ServiceDelayDurationInput.scss";

enum Durations {
    Hours24 = 24,
    Hours36 = 36,
    Hours48 = 48,
    Hours72 = 72,
    Week1 = 7 * 24,
    Weeks2 = 2 * 7 * 24,
    Weeks3 = 3 * 7 * 24,
    Weeks4 = 4 * 7 * 24
}

interface ServiceDelayDurationInputProps {
    withUnit?: boolean;
    label?: ReactNode;
    value?: Timeframe | null;
    onChange?: (value: Timeframe) => void;
}

const defaultValue: Timeframe = { Value: 24, Unit: TimeframeUnit.Hour };

function ServiceDelayDurationInput(props: ServiceDelayDurationInputProps): JSX.Element {
    const { withUnit = true, label, value: duration = { ...defaultValue }, onChange } = props;

    const { getText } = useTranslation();

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            const { name, value } = e.target;
            const key = name as keyof Timeframe;
            const val = !isNaN(parseInt(value)) ? parseInt(value) : null;

            const data: Timeframe = {
                ...(duration ?? defaultValue),
                [key]: val
            };

            if (key === "Value" && val !== null) {
                if (val < 1) {
                    data[key] = 1;
                }
            }

            if (!withUnit) {
                data.Unit = TimeframeUnit.Hour;
            }

            onChange?.(data);
        },
        [withUnit, duration, onChange]
    );

    const renderControl = useMemo(
        () => (
            <div className="form-group">
                <select
                    name="Value"
                    className="form-control"
                    value={TimeframeToHours(duration ?? defaultValue)}
                    onChange={handleChange}
                >
                    {EnumHelper.getNamesAndValues(Durations)
                        .map(pair => {
                            const [text, qty] = pair.name.match(/([0-9]+)|([a-zA-Z]+)/g);
                            const label = `${qty} ${getText(text + "Label")}`;

                            return (
                                <option key={pair.name} value={pair.value}>{label}</option>
                            );
                        })}
                </select>
            </div>
        ),
        [duration?.Value, handleChange, getText]
    );

    const renderControlWithUnit = useMemo(
        () => (
            <>
                <div className="form-group">
                    <input
                        min={1}
                        max={99}
                        maxLength={2}
                        name="Value"
                        type="number"
                        className="form-control"
                        value={duration?.Value ?? ""}
                        onChange={handleChange}
                    />
                </div>

                <div className="form-group">
                    <select
                        name="Unit"
                        className="form-control"
                        value={duration?.Unit ?? defaultValue.Unit}
                        onChange={handleChange}
                    >
                        {EnumHelper.getNamesAndValues(TimeframeUnit)
                            .map(pair => {
                                const label = getText(pair.name + "Label");

                                return (
                                    <option key={pair.name} value={pair.value}>{label}</option>
                                );
                            })}
                    </select>
                </div>
            </>
        ),
        [duration?.Unit, duration?.Value, handleChange, getText]
    );

    return (
        <div className="duration-input">
            {withUnit ? renderControlWithUnit : renderControl}

            <div className="form-group">{label}</div>
        </div>
    );
}

export { ServiceDelayDurationInput };
